import {Issue, useGetValuesByIssueIdQuery} from "@mwServices/Issue";
import React, {useEffect, useMemo, useState} from "react";
import {getRawBucketByGrade} from "@lib/utils";
import {Button, Col, Dropdown, Menu, Row, Spin} from "antd";
import MenuItem from "antd/es/menu/MenuItem";
import {Grades} from "@lib/constants";
import {DownOutlined, RightOutlined} from "@ant-design/icons";
import {formatGrade} from "@lib/format";

interface ComicGradeSelectMenuProps {
    item: any;
    setFmvData: (fmvData: ComicFmvData) => void;
    defaultFmvData: ComicFmvData;
}

export interface ComicFmvData {
    label: string;
    raw: {
        stale: boolean | null;
        grade: string | null;
        value: number | null;
    },
    slabbed: {
        stale: boolean | null;
        grade: string | null;
        value: number | null;
    },
};

export const getDefaultFmvDataFromIssue = (issue): ComicFmvData => {
    const mccIsRaw = issue.graded_or_raw === 'raw';
    const rawBucket = getRawBucketByGrade(issue.raw_fmv_grade);
    return {
        label: mccIsRaw ? formatGrade(rawBucket ? rawBucket.low : null) : issue.slabbed_fmv_grade,
        raw: {
            stale: mccIsRaw ? issue.raw_fmv_stale : issue.paired_stale,
            grade: mccIsRaw ? issue.raw_fmv_grade : issue.paired_grade,
            value: mccIsRaw ? issue.raw_fmv_value : issue.paired_value,
        },
        slabbed: {
            stale: mccIsRaw ? issue.paired_stale : issue.slabbed_fmv_stale,
            grade: mccIsRaw ? issue.paired_grade : issue.slabbed_fmv_grade,
            value: mccIsRaw ? issue.paired_value : issue.slabbed_fmv_value,
        },
    }
};

const ComicGradeSelectMenu = ({item, setFmvData, defaultFmvData}: ComicGradeSelectMenuProps) => {
    const valuesQuery = useGetValuesByIssueIdQuery({issueId: item.id});

    const valuesData = useMemo(() => {
        if (valuesQuery.data) {
            return valuesQuery.data.values;
        }
        return null;
    }, [valuesQuery.data]);

    const isGradeAvailable = (grade) => {
        if(valuesData) {
            if (valuesData.slabbed) {
                const slabbedData = valuesData.slabbed.data;
                if(slabbedData.find((data) => data.grade === grade)) return false;
            }
            if(valuesData.raw){
                const rawBucket = getRawBucketByGrade(grade);
                if(rawBucket){
                    const rawData = valuesData.raw.data;
                    if(rawData.find((data) => data.grade === rawBucket.short_desc)) return false;
                }
            }
        }
        return true;
    }

    const getValueByGrade = (grade) => {
        const returnData: ComicFmvData = {
            label: grade,
            raw: {
                stale: null,
                grade: null,
                value: null,
            },
            slabbed: {
                stale: null,
                grade: null,
                value: null,
            },
        };

        if(valuesData) {
            if (valuesData.slabbed) {
                const slabbedData = valuesData.slabbed.data.find((data) => data.grade === grade);
                if(slabbedData) returnData['slabbed'] = slabbedData;
            }
            if(valuesData.raw){
                const rawBucket = getRawBucketByGrade(grade);
                if(rawBucket){
                    const rawData = valuesData.raw.data.find((data) => data.grade === rawBucket.short_desc);
                    if(rawData) returnData['raw'] = rawData;
                }
            }
        }

        return returnData;
    }

    return (
        <div style={{backgroundColor: 'white'}}>
            <Spin spinning={valuesQuery.isFetching || valuesQuery.isLoading}>
                <Menu
                    style={{maxHeight: 150, overflow: 'auto'}}
                >
                    {Grades.slabbed.map(grade => (
                        <MenuItem
                            key={'select-grade-'+grade.short_desc}
                            disabled={isGradeAvailable(grade.short_desc)}
                            onClick={() => {
                                const slabbed = valuesData && valuesData.slabbed && valuesData.slabbed.data.find((data) => data.grade === grade.short_desc);
                                setFmvData(getValueByGrade(grade.short_desc));
                            }}
                        >
                            {grade.short_desc}
                        </MenuItem>
                    ))}
                </Menu>
            </Spin>
        </div>
    );
};

interface GradeSelectDropdownProps {
    issue: Issue;
    onChange: (fmvData:ComicFmvData) => void;
}

export const ComicGradeSelect = ({issue, onChange}: GradeSelectDropdownProps) => {
    const defaultFmvData :ComicFmvData = useMemo(() => getDefaultFmvDataFromIssue(issue), [issue]);
    const [fmvData, setFmvData] = useState(defaultFmvData);
    useEffect(() => setFmvData(defaultFmvData), [defaultFmvData]);

    useEffect(() => onChange(fmvData), [fmvData]);

    return (
        <Dropdown
            trigger={["click"]}
            className="greyButton selectButton grades-select rounded-3px"
            overlay={(<ComicGradeSelectMenu item={issue} setFmvData={setFmvData} defaultFmvData={defaultFmvData}/>)}
        >
            <Button className={'greyButton selectButton grades-select rounded-3px'} >
                {fmvData.label}
                <DownOutlined style={{float: 'right', marginTop: 3}}/>
            </Button>
        </Dropdown>
    );
};

export default ComicGradeSelect;